import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type Ref } from 'react';
import { quantity } from '@/components/ui';
import {
  contentTotal,
  defaultEntryUnit,
  entryUnits,
  fromItemQty,
  fromTenThousandths,
  plural,
  toItemQty,
  UNIT_KEY,
  type EntryUnit,
  type PackedItem,
} from '@/packing';

/**
 * A quantity box that also takes packages: "3 Cartons", "2 Cartons + 10
 * Packs", or plain kilograms. Whatever is typed, it hands back the quantity
 * in the item's own unit, so the line, the payload, the outbox and the server
 * never see a package. An item with no package is a plain number box, as
 * before.
 *
 * A quantity that cannot be kept exactly (1.5 Cartons of 25 whole Packs) is
 * never rounded: the box is marked invalid with the reason, and nothing is
 * handed back until it is put right.
 */
export function PackedQtyInput({
  item,
  itemKey,
  value,
  onChange,
  onUnitChange,
  label,
  srLabel,
  labelClassName = 'field__label',
  required,
  disabled,
  startWith = 'unit',
  remember,
  inputRef,
  onKeyDown,
  showTotal = true,
  hint,
}: {
  /** The item's unit and packing; null before an item is chosen. */
  item: PackedItem | null | undefined;
  /** Changes when a different item is chosen, so the unit starts afresh. */
  itemKey?: string | undefined;
  /** In the item's own unit. */
  value: string;
  onChange: (qty: string) => void;
  /** The unit being typed in, for a cost box beside it that follows it. */
  onUnitChange?: ((unit: EntryUnit) => void) | undefined;
  label: string;
  /** Said to a screen reader after the label: " for item 2". */
  srLabel?: string | undefined;
  labelClassName?: string | undefined;
  required?: boolean | undefined;
  disabled?: boolean | undefined;
  /** What a newly chosen item starts in: its default package (receiving) or its own unit. */
  startWith?: 'package' | 'unit' | undefined;
  /** A screen name: the unit last used for an item there is offered first next time. */
  remember?: string | undefined;
  inputRef?: Ref<HTMLInputElement> | undefined;
  onKeyDown?: ((e: KeyboardEvent<HTMLInputElement>) => void) | undefined;
  /** "= 150 KG" under a quantity typed in packages. */
  showTotal?: boolean | undefined;
  hint?: React.ReactNode;
}) {
  const units = useMemo(() => (item ? entryUnits(item) : []), [item]);
  const packed = units.length > 1;
  const amountId = useId();

  const startUnit = (): EntryUnit | undefined => {
    if (!item || !packed) return units[0];
    const kept = remember && itemKey ? readRemembered(remember, itemKey) : null;
    const found = kept ? units.find((u) => u.key === kept) : undefined;
    if (found) return found;
    return startWith === 'package' ? defaultEntryUnit(item) : units[0];
  };

  const [start] = useState(() => opening(value, startUnit()));
  const [unitKey, setUnitKey] = useState(start.key);
  const unit = units.find((u) => u.key === unitKey) ?? units[0];
  const [typed, setTyped] = useState(start.typed);
  const sent = useRef(value);
  const amountRef = useRef<HTMLInputElement | null>(null);

  // A different item, or its packing arriving after the line (a draft opened
  // before the item list loaded): start again in its own starting unit. The
  // unit in use is reported either way, so a cost box beside can follow it.
  const signature = `${itemKey ?? ''}|${units.map((u) => `${u.key}=${u.size}`).join(',')}`;
  const lastSignature = useRef<string | null>(null);
  useEffect(() => {
    const before = lastSignature.current;
    lastSignature.current = signature;
    // Kept: the first render (already opened by useState), or the same item
    // whose packing was known and still has the unit in use.
    const known = before !== null && before.split('|')[0] === (itemKey ?? '') && before.split('|')[1] !== '';
    if (before === null || (known && units.some((u) => u.key === unitKey))) {
      if (unit) onUnitChange?.(unit);
      return;
    }
    const next = opening(value, startUnit());
    setUnitKey(next.key);
    setTyped(next.typed);
    const chosen = units.find((u) => u.key === next.key);
    if (chosen) onUnitChange?.(chosen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  // The quantity changed from outside (a draft restored, a line repeated):
  // shown in the current unit when it divides, else in the item's own.
  useEffect(() => {
    if (value === sent.current) return;
    sent.current = value;
    const inUnit = unit ? wholeIn(value, unit) : null;
    if (value === '' || inUnit) {
      setTyped(value === '' ? { amount: '', packs: '' } : inUnit!);
    } else {
      setUnitKey(UNIT_KEY);
      setTyped({ amount: value, packs: '' });
      if (units[0]) onUnitChange?.(units[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // An item with no package is a plain box: what is typed is the quantity,
  // checked on save as it always was.
  const result = item && unit && packed ? toItemQty(item, unit, typed.amount, typed.packs) : null;
  const problem = result && 'problem' in result ? result.problem : null;

  useEffect(() => {
    amountRef.current?.setCustomValidity(problem ?? '');
  }, [problem]);

  function send(next: { amount: string; packs: string }, inUnit = unit) {
    setTyped(next);
    if (!item || !inUnit || !packed) {
      sent.current = next.amount;
      onChange(next.amount);
      return;
    }
    const r = toItemQty(item, inUnit, next.amount, next.packs);
    const qty = r && 'qty' in r ? r.qty : '';
    sent.current = qty;
    onChange(qty);
  }

  function chooseUnit(key: string) {
    const next = units.find((u) => u.key === key);
    if (!next) return;
    setUnitKey(key);
    if (remember && itemKey) writeRemembered(remember, itemKey, key);
    onUnitChange?.(next);
    // The number stays and is read in the new unit: people type "3" and
    // then say what of.
    send({ amount: typed.amount, packs: next.withPacks ? typed.packs : '' }, next);
  }

  const setRef = (el: HTMLInputElement | null) => {
    amountRef.current = el;
    if (typeof inputRef === 'function') inputRef(el);
    else if (inputRef) (inputRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
  };

  const numberBox = (props: { id?: string; value: string; onChange: (v: string) => void; aria?: string; first?: boolean }) => (
    <input
      {...(props.first ? { ref: setRef } : {})}
      {...(props.id ? { id: props.id } : {})}
      className="field__input num"
      type="number"
      step="any"
      min="0"
      inputMode="decimal"
      required={props.first ? required : false}
      disabled={disabled}
      value={props.value}
      {...(props.aria ? { 'aria-label': props.aria } : {})}
      {...(props.first && problem ? { 'aria-invalid': true } : {})}
      onChange={(e) => props.onChange(e.target.value)}
      onKeyDown={onKeyDown}
    />
  );

  const total =
    item && packed && showTotal && unit && unit.key !== UNIT_KEY && result && 'qty' in result
      ? `= ${quantity(result.qty, item.uomPrecision)} ${item.uom}`
      : null;
  const holds =
    item && showTotal && result && 'qty' in result ? contentTotal(result.qty, item, (v, p) => quantity(v, p)) : null;
  // Before anything is typed, what one of the chosen package is: the choice
  // itself only names it, so it fits a narrow quantity column.
  const sizeNote = item && packed && unit && unit.key !== UNIT_KEY && !result ? sizeOf(unit, item) : null;

  return (
    <div className="field packedqty">
      <label className={labelClassName} htmlFor={amountId}>
        {label}
        {srLabel && <span className="sr-only">{srLabel}</span>}
        {required && labelClassName === 'field__label' && <span aria-hidden="true"> *</span>}
      </label>
      <div className={packed ? 'packedqty__row' : undefined}>
        {numberBox({
          id: amountId,
          first: true,
          value: typed.amount,
          onChange: (amount) => send({ amount, packs: typed.packs }),
        })}
        {packed && unit?.withPacks &&
          numberBox({
            value: typed.packs,
            aria: `${plural(unit.packaging!.innerName!, 2)}${srLabel ?? ''}`,
            onChange: (packs) => send({ amount: typed.amount, packs }),
          })}
        {packed && (
          <select
            className="field__input packedqty__unit"
            aria-label={`Unit${srLabel ?? ''}`}
            disabled={disabled}
            value={unit?.key ?? UNIT_KEY}
            onChange={(e) => chooseUnit(e.target.value)}
          >
            {units.map((u) => (
              <option key={u.key} value={u.key}>
                {u.key === UNIT_KEY ? u.label : unitOption(u, units)}
              </option>
            ))}
          </select>
        )}
      </div>
      {problem ? (
        <span className="itemlines__hint errornote" role="alert">
          {problem}
        </span>
      ) : (
        (total || holds || sizeNote) && (
          <span className="itemlines__hint packedqty__total" aria-live="polite">
            {sizeNote ?? [total, holds && (total ? holds : `= ${holds}`)].filter(Boolean).join(' · ')}
          </span>
        )
      )}
      {hint && <span className="settings__hint">{hint}</span>}
    </div>
  );
}

/**
 * A choice by name alone, "Carton" or "Cartons + Packs", so it fits a narrow
 * column; the size is said under the box. Two choices sharing a name (a 1 kg
 * and a 5 kg Pack) carry their size to tell them apart.
 */
function unitOption(u: EntryUnit, units: readonly EntryUnit[]): string {
  if (u.withPacks) return `${plural(u.packaging!.name, 2)} + ${plural(u.packaging!.innerName!, 2)}`;
  const shared = units.filter((o) => !o.withPacks && o.label.toLowerCase() === u.label.toLowerCase()).length > 1;
  return shared ? `${u.label} of ${fromTenThousandths(u.size)}` : u.label;
}

/** "1 Carton = 50 KG", "1 Carton = 50 Packs = 50 KG". */
function sizeOf(u: EntryUnit, item: PackedItem): string {
  const p = u.packaging;
  const inside = u.withPacks && p?.innerName && p.innerCount ? `${p.innerCount} ${plural(p.innerName, p.innerCount)} = ` : '';
  const name = u.withPacks && p ? p.name : u.label;
  return `1 ${name} = ${inside}${fromTenThousandths(u.size)} ${item.uom}`;
}

/**
 * The unit and figures a quantity opens in: `unit` when the quantity divides
 * into it (150 KG opens as 3 Cartons), else the item's own unit, so a stored
 * 7 KG is never shown as 7 Cartons.
 */
function opening(value: string, unit: EntryUnit | undefined): { key: string; typed: { amount: string; packs: string } } {
  if (!unit) return { key: UNIT_KEY, typed: { amount: value, packs: '' } };
  if (value === '') return { key: unit.key, typed: { amount: '', packs: '' } };
  const shown = wholeIn(value, unit);
  return shown ? { key: unit.key, typed: shown } : { key: UNIT_KEY, typed: { amount: value, packs: '' } };
}

/** A quantity in `unit` when it comes to whole packages and packs; any figure in the item's own unit. */
function wholeIn(value: string, unit: EntryUnit): { amount: string; packs: string } | null {
  const shown = fromItemQty(value, unit);
  if (!shown || unit.key === UNIT_KEY) return shown;
  return /^\d+$/.test(shown.amount) && /^\d*$/.test(shown.packs) ? shown : null;
}

const rememberKey = (screen: string, itemKey: string) => `packedUnit:${screen}:${itemKey}`;

function readRemembered(screen: string, itemKey: string): string | null {
  try {
    return localStorage.getItem(rememberKey(screen, itemKey));
  } catch {
    return null;
  }
}

function writeRemembered(screen: string, itemKey: string, unitKey: string) {
  try {
    localStorage.setItem(rememberKey(screen, itemKey), unitKey);
  } catch {
    // A full or blocked store only loses the preference.
  }
}
